import Footer from "./Footer"; // Import Footer component
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  LabelList,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
const introLogo = "/reportImages/intro-logo.png";

const RiverFlowsSection = ({ data }) => {
  const chartData = data?.slice(0, 3).map((entry) => ({
    period: entry?.period_date,
    inflow: (entry.system_inflows?.yearly_comparision / 1000).toFixed(2),
  }));

  const renderValue = (val, fixed = 2, suffix = "") => {
    if (val === "-" || val === undefined || val === null || isNaN(val))
      return "-";
    const num = Number(val);
    const isNegative = num < 0 || Object.is(num, -0);
    return (
      <span style={{ color: isNegative ? "#DC2626" : undefined }}>
        {isNegative ? "-" : ""}
        {Math.abs(num).toLocaleString(undefined, {
          minimumFractionDigits: fixed,
          maximumFractionDigits: fixed,
        })}
        {suffix}
      </span>
    );
  };

  const renderChangeValue = (val, isPercent = false) => {
    if (val === "-" || val === undefined || val === null || isNaN(Number(val)))
      return "-";
    const num = Number(val);
    const isNegative = num < 0 || Object.is(num, -0);
    const fontSize = Math.abs(num) >= 1000 ? "0.95em" : "inherit";

    return (
      <span
        style={{
          color: isNegative ? "#DC2626" : undefined,
          fontSize: fontSize,
        }}
      >
        {isNegative ? "-" : ""}
        {Math.abs(num).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
        {isPercent ? "%" : ""}
      </span>
    );
  };

  const tableRows = data?.slice(0, 3).map((entry, idx) => {
    const date = entry?.period_date;
    const tarbela = entry["Indus@Tarbela"] || {};
    const nowshera = entry["Kabul@Noshera"] || {};
    const mangla = entry["Jhelum@Mangla"] || {};
    const marala = entry["Chenab@Marala"] || {};
    const sys = entry.system_inflows || {};

    return (
      <tr key={idx}>
        <td className="px-0">{date}</td>
        <td>{renderValue(tarbela.levels?.value)}</td>
        <td>{renderValue(tarbela.inflow?.value / 1000)}</td>
        <td>{renderValue(tarbela.outflow?.value / 1000)}</td>
        <td>{renderValue(nowshera.inflow?.value / 1000)}</td>
        <td>{renderValue(mangla.levels?.value)}</td>
        <td>{renderValue(mangla.inflow?.value / 1000)}</td>
        <td>{renderValue(mangla.outflow?.value / 1000)}</td>
        <td>{renderValue(marala.inflow?.value / 1000)}</td>
        <td>{renderValue(sys.yearly_comparision / 1000)}</td>
        <td>{renderValue(sys.last_ten_year_avg / 1000)}</td>
      </tr>
    );
  });

  const changeHeaderRow = (currentYear, previousYear) => (
    <tr>
      <td
        colSpan={11}
        style={{
          borderTop: "1.5px solid #ffffff",
          backgroundColor: "#AAAAAA",
          color: "#000000",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "center",
          // padding: "5px",
          margin: "0",
        }}
      >
        Change {currentYear} over {previousYear}
      </td>
    </tr>
  );

  const changeDataRow = (field, label) => {
    if (data?.length < 2) return null;
    const current = data?.[0];
    const previous = data?.[1];

    const getVal = (curr, prev, station, metric, type) => {
      const currMetric = curr?.[station]?.[metric];
      if (!currMetric) return "-";
      if (type === "percentChange") {
        return currMetric.percentChange !== undefined
          ? renderChangeValue(currMetric.percentChange, true)
          : "-";
      } else if (type === "change") {
        // For levels, do not divide by 1000
        if (metric === "levels" && currMetric.change !== undefined)
          return renderValue(currMetric.change, 2);
        if (currMetric.change !== undefined)
          return renderValue(currMetric.change / 1000, 2);
        return "-";
      }
      return "-";
    };

    const getSys = (curr, prev, key, type) => {
      const currVal = curr?.system_inflows?.[key];
      const prevVal = prev?.system_inflows?.[key];
      if (currVal === undefined || prevVal === undefined) return "-";
      if (type === "percentChange") {
        if (
          typeof currVal === "object" &&
          currVal.percentChange !== undefined
        ) {
          return renderChangeValue(Number(currVal.percentChange), true);
        }
        if (prevVal === 0) return "-";
        const percent = ((currVal - prevVal) / prevVal) * 100;
        return renderChangeValue(percent, true);
      } else if (type === "change") {
        if (typeof currVal === "object" && currVal.change !== undefined) {
          return renderValue(currVal.change / 1000, 2);
        }
        return renderValue((currVal - prevVal) / 1000, 2);
      }
      return "-";
    };

    return (
      <tr className={field === "percentChange" ? "percent-row" : "change-row"}>
        <td>{label === "Absolute Change" ? "" : label}</td>
        <td>{getVal(current, previous, "Indus@Tarbela", "levels", field)}</td>
        <td>{getVal(current, previous, "Indus@Tarbela", "inflow", field)}</td>
        <td>{getVal(current, previous, "Indus@Tarbela", "outflow", field)}</td>
        <td>{getVal(current, previous, "Kabul@Noshera", "inflow", field)}</td>
        <td>{getVal(current, previous, "Jhelum@Mangla", "levels", field)}</td>
        <td>{getVal(current, previous, "Jhelum@Mangla", "inflow", field)}</td>
        <td>{getVal(current, previous, "Jhelum@Mangla", "outflow", field)}</td>
        <td>{getVal(current, previous, "Chenab@Marala", "inflow", field)}</td>
        <td>{getSys(current, previous, "yearly_comparision", field)}</td>
        <td>{getSys(current, previous, "last_ten_year_avg", field)}</td>
      </tr>
    );
  };

  return (
    <div id="river-flows-and-levels">
     <div id="synoptic-section">
        <h4 className="synoptic-section-heading">
          <span className="section-number">3</span>
          <span className="section-title-text">Weekly Reservoirs Outlook</span>
        </h4>
      </div>
      <div className="river-section">
        <h2 className="section-title">3a. River Flows and Levels</h2>
        <ul className="summary-list">
          {/* Bullet 1: Tarbela Levels */}
          <li>
            On {data?.[0]?.period_date || "latest date"}, water levels in the
            Indus River at Tarbela Dam reached{" "}
            {renderValue(data?.[0]?.["Indus@Tarbela"]?.levels?.value, 2)} feet,
            {/* 
              The following span uses a nested ternary to determine the className:
              - If the latest value is greater than the value 3 periods ago: "increased"
              - If less: "decreased"
              - If equal or missing: "neutral"
              This controls the color of the text (green/red/neutral) via CSS.
              */}
            {/* 
                The label text also uses a nested ternary:
                - "an increase" if latest > previous
                - "a decrease" if latest < previous
                - "no change" otherwise
              */}{" "}
            which is{" "}
            <span>
              {(data?.[0]?.["Indus@Tarbela"]?.levels?.value || 0) >
              (data?.[3]?.["Indus@Tarbela"]?.levels?.value || 0) ? (
                <>
                  an <span className="increased">increase </span>
                </>
              ) : (data?.[0]?.["Indus@Tarbela"]?.levels?.value || 0) <
                (data?.[3]?.["Indus@Tarbela"]?.levels?.value || 0) ? (
                <>
                  a <span className="decreased">decrease </span>
                </>
              ) : (
                <span className="neutral">no change </span>
              )}
            </span>
            from {renderValue(data?.[3]?.["Indus@Tarbela"]?.levels?.value, 2)}{" "}
            feet recorded on {data?.[3]?.period_date || "previous date"}. This
            level is{" "}
            {renderValue(
              (data?.[0]?.["Indus@Tarbela"]?.levels?.value || 0) - 1402,
              2
            )}{" "}
            feet above the dead level, reflecting a{" "}
            {renderValue(
              Math.abs(
                (data?.[0]?.["Indus@Tarbela"]?.levels?.value || 0) -
                  (data?.[3]?.["Indus@Tarbela"]?.levels?.value || 0)
              ),
              2
            )}{" "}
            foot{" "}
            {/* 
              Again, a nested ternary for className and label for the change direction.
              This is repeated to emphasize the direction of change in both color and text.
            */}
            <span>
              {(data?.[0]?.["Indus@Tarbela"]?.levels?.value || 0) >
              (data?.[3]?.["Indus@Tarbela"]?.levels?.value || 0) ? (
                <>
                  an <span className="increased">increase </span>
                </>
              ) : (data?.[0]?.["Indus@Tarbela"]?.levels?.value || 0) <
                (data?.[3]?.["Indus@Tarbela"]?.levels?.value || 0) ? (
                <>
                  a <span className="decreased">decrease </span>
                </>
              ) : (
                <span className="neutral">no change </span>
              )}
            </span>
            from last week.
          </li>

          {/* Bullet 2: System Inflows */}
          <li>
            The current year's water available in the system flows{" "}
            {/* 
              This span uses a similar nested ternary to determine the className:
              - "increased" if current inflow > previous inflow
              - "decreased" if current inflow < previous inflow
              - "neutral" otherwise
            */}
            <span
              className={
                (data?.[0]?.system_inflows?.yearly_comparision || 0) >
                (data?.[1]?.system_inflows?.yearly_comparision || 0)
                  ? "increased"
                  : (data?.[0]?.system_inflows?.yearly_comparision || 0) <
                    (data?.[1]?.system_inflows?.yearly_comparision || 0)
                  ? "decreased"
                  : "neutral"
              }
            >
              {/* 
                The label text also uses a nested ternary:
                - "increased" if current > previous
                - "decreased" if current < previous
                - "remained unchanged" otherwise
              */}
              {(data?.[0]?.system_inflows?.yearly_comparision || 0) >
              (data?.[1]?.system_inflows?.yearly_comparision || 0)
                ? "increased"
                : (data?.[0]?.system_inflows?.yearly_comparision || 0) <
                  (data?.[1]?.system_inflows?.yearly_comparision || 0)
                ? "decreased"
                : "remained unchanged"}
            </span>{" "}
            from {renderValue(data?.[1]?.system_inflows?.yearly_comparision, 0)}{" "}
            cusecs ({new Date(data?.[1]?.period_date || "").getFullYear()}) to{" "}
            {renderValue(data?.[0]?.system_inflows?.yearly_comparision, 0)}{" "}
            cusecs ({new Date(data?.[0]?.period_date || "").getFullYear()}),
            representing a{" "}
            {/* 
              This renderValue call calculates the percent change between current and previous inflows.
              It uses a nested Math.abs and division, with fallback to 1 to avoid division by zero.
            */}
            {renderValue(
              Math.abs(
                (((data?.[0]?.system_inflows?.yearly_comparision || 0) -
                  (data?.[1]?.system_inflows?.yearly_comparision || 0)) /
                  (data?.[1]?.system_inflows?.yearly_comparision || 1)) *
                  100
              ),
              2,
              "%"
            )}{" "}
            {/* 
              Another nested ternary for className and label for the direction of percent change.
              This ensures both color and text match the direction of change.
            */}
            <span
              className={
                (data?.[0]?.system_inflows?.yearly_comparision || 0) >
                (data?.[1]?.system_inflows?.yearly_comparision || 0)
                  ? "increased"
                  : (data?.[0]?.system_inflows?.yearly_comparision || 0) <
                    (data?.[1]?.system_inflows?.yearly_comparision || 0)
                  ? "decreased"
                  : "neutral"
              }
            >
              {(data?.[0]?.system_inflows?.yearly_comparision || 0) >
              (data?.[1]?.system_inflows?.yearly_comparision || 0)
                ? "increase"
                : (data?.[0]?.system_inflows?.yearly_comparision || 0) <
                  (data?.[1]?.system_inflows?.yearly_comparision || 0)
                ? "decrease"
                : "no change"}
            </span>{" "}
            compared to the previous year.
          </li>
        </ul>
        <p className="summary-list-paragraph">
          A comparative position of river flow levels, as of{" "}
          {data?.[0]?.period_date}, along with the corresponding positions for{" "}
          {new Date(data?.[2]?.period_date || "").getFullYear()} and{" "}
          {new Date(data?.[1]?.period_date || "").getFullYear()} is summarized
          and provided on the following page.
        </p>
        <h6 className="toc-heading">
          River Flows & Levels: Irrigation Inflows & Outflows
        </h6>
        <h6 className="riverflow-heading">(All Figures in 1000 x CUSECS)</h6>

        <table className="table table-striped comparison-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: "70px" }}>
                Period Date
              </th>
              <th colSpan={3} style={{ width: "170px" }}>
                Indus at Tarbela
              </th>
              <th rowSpan={2} style={{ paddingLeft: "0", paddingRight: "0" }}>
                Kabul at Nowshera
              </th>
              <th colSpan={3} style={{ width: "170px" }}>
                Jhelum at Mangla
              </th>
              <th rowSpan={2}>Chenab at Marala</th>
              <th colSpan={2} style={{ width: "170px" }}>
                System Inflows
              </th>
            </tr>
            <tr>
              <th>Levels</th>
              <th>Inflow</th>
              <th>Outflow</th>
              <th>Levels</th>
              <th>Inflow</th>
              <th>Outflow</th>
              <th>Yearly Comparison</th>
              <th>10-Year Avg</th>
            </tr>
          </thead>
          <tbody>
            {tableRows}
            {changeHeaderRow(
              new Date(data?.[0]?.period_date).getFullYear(),
              new Date(data?.[1]?.period_date).getFullYear()
            )}
            {changeDataRow("change", "Absolute Change")}
            {changeDataRow("percentChange", "% Change")}
          </tbody>
        </table>
        <p className="source-text">Source: Pakistan Agriculture Research</p>

        <h4 className="image-caption" style={{ marginTop: 30 }}>
          Comparison of River Inflows (in Thousand Cusecs)
        </h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart
            data={chartData}
            barSize={30}
            barGap={0}
            barCategoryGap="5%"
            layout="vertical"
            margin={{ bottom: 50, right: 70, left: 50, top: 30 }}
            // margin={{ top: 50, right: 40 }}
          >
            <XAxis
              type="number"
              fontSize={10}
              tickLine={false}
              stroke="#000000"
              strokeWidth={1.2}
            />
            <YAxis
              dataKey="period"
              type="category"
              fontSize={10}
              width={80}
              hide={true}
            />
            <ReferenceLine x={0} stroke="#888" strokeWidth={2} />

            <Tooltip />
            <Legend
              iconSize={8}
              payload={chartData?.map((item, idx) => ({
                value: item.period,
                type: "circle",
                id: item.period,
                color:
                  idx === 0
                    ? "#2F8DCC" // First bar color
                    : idx === 1
                    ? "#2F8DCC99" // Second bar color
                    : "#6EB0DC66", // Third bar color
              }))}
            />
            <Bar dataKey="inflow" name="System Inflows">
              <LabelList
                dataKey="inflow"
                position="right"
                fill="#000000"
                stroke="#000000"
                strokeWidth={0.5}
                fontSize={10}
              />
              {chartData?.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    index === 0
                      ? "#2F8DCC" // First bar color
                      : index === 1
                      ? "#2F8DCC99" // Second bar color
                      : "#6EB0DC66" // Third bar color
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div style={{ marginTop: "110px" }}>
          <Footer footerNumber="5" footerText="Weekly Water & Weather Report" />
        </div>
      </div>
    </div>
  );
};

export default RiverFlowsSection;
